/**
 * Canonical booking / payment / cancellation policy helpers.
 *
 * Mirrors the SQL resolution in
 * `supabase/migrations/20260918053000_booking_payment_policies.sql`.
 * React components must use these helpers (or values returned by the SQL RPCs)
 * instead of encoding clinic-specific payment rules.
 */

export type PaymentTiming = 'upon_booking' | 'at_appointment';

export type PatientCancelPermission = 'allow' | 'deny';

export type CardCancelAction = 'no_refund' | 'full_refund' | 'refund_minus_fee';

export type InvoiceUnpaidAction = 'leave' | 'void' | 'cancel';
export type InvoicePaidAction = 'no_refund' | 'full_refund';
export type InvoicePartialAction = 'no_refund' | 'refund_paid_amount' | 'leave';

export type PunchCardCancelAction = 'forfeit' | 'restore';

export type BookingPaymentMethodKey = 'card' | 'mobilepay' | 'invoice' | 'punch_card';

export interface BookingPaymentMethods {
  card: boolean;
  mobilepay: boolean;
  invoice: boolean;
  punch_card: boolean;
}

export interface CancellationFee {
  type: 'none' | 'fixed' | 'percent';
  amount_cents?: number;
  percent?: number;
}

export interface DeadlineFinancialRule {
  action: CardCancelAction;
  fee?: CancellationFee;
}

export interface CardCancellationPolicy {
  before_deadline: DeadlineFinancialRule;
  after_deadline: DeadlineFinancialRule;
}

export interface InvoiceCancellationPolicy {
  unpaid: { action: InvoiceUnpaidAction };
  paid: { action: InvoicePaidAction };
  partial: { action: InvoicePartialAction };
}

export interface PunchCardCancellationPolicy {
  before_deadline: { action: PunchCardCancelAction };
  after_deadline: { action: PunchCardCancelAction };
}

export const DEFAULT_BOOKING_PAYMENT_METHODS: BookingPaymentMethods = {
  card: true,
  mobilepay: true,
  invoice: true,
  punch_card: true,
};

export const DEFAULT_CARD_CANCELLATION_POLICY: CardCancellationPolicy = {
  before_deadline: { action: 'no_refund' },
  after_deadline: { action: 'no_refund' },
};

export const DEFAULT_INVOICE_CANCELLATION_POLICY: InvoiceCancellationPolicy = {
  unpaid: { action: 'leave' },
  paid: { action: 'no_refund' },
  partial: { action: 'no_refund' },
};

export const DEFAULT_PUNCH_CARD_CANCELLATION_POLICY: PunchCardCancellationPolicy = {
  before_deadline: { action: 'forfeit' },
  after_deadline: { action: 'forfeit' },
};

export const DEFAULT_PAYMENT_TIMING: PaymentTiming = 'at_appointment';
export const DEFAULT_CANCELLATION_DEADLINE_HOURS = 24;
export const DEFAULT_ALLOW_PATIENT_RESCHEDULING = false;

export interface PatientPortalClinicPolicy {
  deadlineHours: number;
  allowPatientRescheduling: boolean;
  beforeDeadline: PatientCancelPermission;
  afterDeadline: PatientCancelPermission;
}

/**
 * Portal list queries must keep working when booking-policy columns are not
 * in the PostgREST schema yet. Missing values use the same defaults as SQL.
 */
export function resolvePatientPortalClinicPolicy(input?: {
  cancellation_deadline_hours?: number | null;
  allow_patient_rescheduling?: boolean | null;
  patient_cancel_before_deadline?: string | null;
  patient_cancel_after_deadline?: string | null;
} | null): PatientPortalClinicPolicy {
  return {
    deadlineHours:
      typeof input?.cancellation_deadline_hours === 'number'
        ? input.cancellation_deadline_hours
        : DEFAULT_CANCELLATION_DEADLINE_HOURS,
    allowPatientRescheduling: Boolean(input?.allow_patient_rescheduling),
    beforeDeadline: input?.patient_cancel_before_deadline === 'deny' ? 'deny' : 'allow',
    afterDeadline: input?.patient_cancel_after_deadline === 'allow' ? 'allow' : 'deny',
  };
}

export function isPostgrestMissingColumnError(error: {
  code?: string;
  message?: string;
} | null | undefined): boolean {
  if (!error) return false;
  if (error.code === 'PGRST204' || error.code === '42703') return true;
  const message = (error.message ?? '').toLowerCase();
  return (
    message.includes('schema cache') ||
    (message.includes('column') && message.includes('does not exist'))
  );
}

export function assertNever(value: never): never {
  throw new Error(`Unhandled policy variant: ${String(value)}`);
}

export function parsePaymentTiming(value: unknown): PaymentTiming {
  if (value === 'upon_booking' || value === 'at_appointment') return value;
  return DEFAULT_PAYMENT_TIMING;
}

export function parseOptionalPaymentTiming(value: unknown): PaymentTiming | null {
  if (value === 'upon_booking' || value === 'at_appointment') return value;
  return null;
}

export function parseBookingPaymentMethods(value: unknown): BookingPaymentMethods {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_BOOKING_PAYMENT_METHODS };
  }
  const raw = value as Record<string, unknown>;
  return {
    card: typeof raw.card === 'boolean' ? raw.card : true,
    mobilepay: typeof raw.mobilepay === 'boolean' ? raw.mobilepay : true,
    invoice: typeof raw.invoice === 'boolean' ? raw.invoice : true,
    punch_card: typeof raw.punch_card === 'boolean' ? raw.punch_card : true,
  };
}

function parseFee(value: unknown): CancellationFee | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const type = raw.type;
  if (type !== 'none' && type !== 'fixed' && type !== 'percent') return undefined;
  return {
    type,
    amount_cents: typeof raw.amount_cents === 'number' ? raw.amount_cents : 0,
    percent: typeof raw.percent === 'number' ? raw.percent : 0,
  };
}

function parseCardRule(value: unknown, fallback: DeadlineFinancialRule): DeadlineFinancialRule {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fallback;
  const raw = value as Record<string, unknown>;
  const action = raw.action;
  if (action !== 'no_refund' && action !== 'full_refund' && action !== 'refund_minus_fee') {
    return fallback;
  }
  return { action, fee: parseFee(raw.fee) };
}

export function parseCardCancellationPolicy(value: unknown): CardCancellationPolicy {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      before_deadline: { ...DEFAULT_CARD_CANCELLATION_POLICY.before_deadline },
      after_deadline: { ...DEFAULT_CARD_CANCELLATION_POLICY.after_deadline },
    };
  }
  const raw = value as Record<string, unknown>;
  return {
    before_deadline: parseCardRule(raw.before_deadline, DEFAULT_CARD_CANCELLATION_POLICY.before_deadline),
    after_deadline: parseCardRule(raw.after_deadline, DEFAULT_CARD_CANCELLATION_POLICY.after_deadline),
  };
}

export function parseInvoiceCancellationPolicy(value: unknown): InvoiceCancellationPolicy {
  const fallback = {
    unpaid: { ...DEFAULT_INVOICE_CANCELLATION_POLICY.unpaid },
    paid: { ...DEFAULT_INVOICE_CANCELLATION_POLICY.paid },
    partial: { ...DEFAULT_INVOICE_CANCELLATION_POLICY.partial },
  };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fallback;
  const raw = value as Record<string, unknown>;
  const unpaid = (raw.unpaid as Record<string, unknown> | undefined)?.action;
  const paid = (raw.paid as Record<string, unknown> | undefined)?.action;
  const partial = (raw.partial as Record<string, unknown> | undefined)?.action;
  return {
    unpaid: {
      action:
        unpaid === 'leave' || unpaid === 'void' || unpaid === 'cancel'
          ? unpaid
          : fallback.unpaid.action,
    },
    paid: {
      action: paid === 'no_refund' || paid === 'full_refund' ? paid : fallback.paid.action,
    },
    partial: {
      action:
        partial === 'no_refund' || partial === 'refund_paid_amount' || partial === 'leave'
          ? partial
          : fallback.partial.action,
    },
  };
}

export function parsePunchCardCancellationPolicy(value: unknown): PunchCardCancellationPolicy {
  const fallback = {
    before_deadline: { ...DEFAULT_PUNCH_CARD_CANCELLATION_POLICY.before_deadline },
    after_deadline: { ...DEFAULT_PUNCH_CARD_CANCELLATION_POLICY.after_deadline },
  };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fallback;
  const raw = value as Record<string, unknown>;
  const before = (raw.before_deadline as Record<string, unknown> | undefined)?.action;
  const after = (raw.after_deadline as Record<string, unknown> | undefined)?.action;
  return {
    before_deadline: {
      action: before === 'forfeit' || before === 'restore' ? before : fallback.before_deadline.action,
    },
    after_deadline: {
      action: after === 'forfeit' || after === 'restore' ? after : fallback.after_deadline.action,
    },
  };
}

/**
 * Service override → legacy require_prepayment → clinic default.
 * Must stay aligned with public.resolve_payment_timing.
 */
export function resolvePaymentTiming(input: {
  clinicPaymentTiming?: unknown;
  servicePaymentTiming?: unknown;
  serviceRequirePrepayment?: boolean | null;
}): PaymentTiming {
  const serviceTiming = parseOptionalPaymentTiming(input.servicePaymentTiming);
  if (serviceTiming) return serviceTiming;
  if (input.serviceRequirePrepayment) return 'upon_booking';
  return parsePaymentTiming(input.clinicPaymentTiming);
}

export function resolvePrepaymentRequired(input: {
  paymentTiming: PaymentTiming;
  depositAmountCents?: number | null;
  priceCents?: number | null;
  payingWithPunchCard?: boolean;
}): boolean {
  if (input.payingWithPunchCard) return false;
  if (input.paymentTiming !== 'upon_booking') return false;
  return resolveChargeAmountCents(input) > 0;
}

export function resolveChargeAmountCents(input: {
  depositAmountCents?: number | null;
  priceCents?: number | null;
}): number {
  const deposit = input.depositAmountCents ?? 0;
  if (deposit > 0) return deposit;
  return Math.max(0, input.priceCents ?? 0);
}

export function isPatientCancellationAllowed(input: {
  appointmentStatus: string;
  startsAt: Date | string;
  now?: Date;
  deadlineHours?: number | null;
  beforeDeadline?: PatientCancelPermission | null;
  afterDeadline?: PatientCancelPermission | null;
}): boolean {
  if (input.appointmentStatus !== 'upcoming') return false;
  const startsAt = input.startsAt instanceof Date ? input.startsAt : new Date(input.startsAt);
  const now = input.now ?? new Date();
  const hoursUntil = (startsAt.getTime() - now.getTime()) / 3_600_000;
  const deadline = input.deadlineHours ?? DEFAULT_CANCELLATION_DEADLINE_HOURS;
  const before = hoursUntil >= deadline;
  const permission = before
    ? (input.beforeDeadline ?? 'allow')
    : (input.afterDeadline ?? 'deny');
  switch (permission) {
    case 'allow':
      return true;
    case 'deny':
      return false;
    default:
      return assertNever(permission);
  }
}

export function isPatientReschedulingAllowed(input: {
  appointmentStatus: string;
  startsAt: Date | string;
  now?: Date;
  allowPatientRescheduling?: boolean | null;
  deadlineHours?: number | null;
}): boolean {
  if (!input.allowPatientRescheduling) return false;
  if (input.appointmentStatus !== 'upcoming') return false;
  const startsAt = input.startsAt instanceof Date ? input.startsAt : new Date(input.startsAt);
  const now = input.now ?? new Date();
  const hoursUntil = (startsAt.getTime() - now.getTime()) / 3_600_000;
  const deadline = input.deadlineHours ?? DEFAULT_CANCELLATION_DEADLINE_HOURS;
  return hoursUntil >= deadline;
}

export function computeRefundAmountCents(input: {
  paidCents: number;
  action: CardCancelAction | InvoicePaidAction | InvoicePartialAction;
  fee?: CancellationFee;
}): number {
  if (input.paidCents <= 0) return 0;
  switch (input.action) {
    case 'no_refund':
    case 'leave':
      return 0;
    case 'full_refund':
    case 'refund_paid_amount':
      return input.paidCents;
    case 'refund_minus_fee': {
      const fee = input.fee;
      if (!fee || fee.type === 'none') return input.paidCents;
      if (fee.type === 'fixed') {
        return Math.max(0, input.paidCents - Math.max(0, fee.amount_cents ?? 0));
      }
      if (fee.type === 'percent') {
        const feeCents = Math.round(input.paidCents * Math.max(0, fee.percent ?? 0) / 100);
        return Math.max(0, input.paidCents - feeCents);
      }
      return assertNever(fee.type);
    }
    default:
      return assertNever(input.action);
  }
}

export function paymentMethodIsEnabled(
  methods: BookingPaymentMethods,
  key: BookingPaymentMethodKey,
): boolean {
  switch (key) {
    case 'card':
      return methods.card;
    case 'mobilepay':
      return methods.mobilepay;
    case 'invoice':
      return methods.invoice;
    case 'punch_card':
      return methods.punch_card;
    default:
      return assertNever(key);
  }
}

export function formatClinicContact(input: {
  email?: string | null;
  phone?: string | null;
}): string {
  return [input.email?.trim(), input.phone?.trim()].filter((part): part is string => Boolean(part)).join(' · ');
}
